import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { uuid } from '@elfsight/lib-utils';

interface MountingInventoryProps {
  widgetRoot: Element;
  children?: ReactNode;
}

const MIWRID_ATTRIBUTE_NAME = 'data-miwrid';

export function MountingInventory({
  widgetRoot,
  children
}: MountingInventoryProps) {
  const rootsRef = useRef(new Set<Element | ShadowRoot>());
  const [widgetRootId] = useState(() => uuid());

  const rootAddedCallbacksRef = useRef(new Set<RootAddedCallback>());
  const rootRemovedCallbacksRef = useRef(new Set<RootRemovedCallback>());

  const api: MountingInventoryAPI = useMemo(() => {
    function addRoot(root: Element | ShadowRoot) {
      rootsRef.current.add(root);
      rootAddedCallbacksRef.current.forEach((callback) => {
        callback(root);
      });

      return function removeRoot() {
        rootsRef.current.delete(root);
        rootRemovedCallbacksRef.current.forEach((callback) => {
          callback(root);
        });
      };
    }

    function getRoots() {
      return rootsRef.current.values();
    }

    function getWidgetRoot() {
      return widgetRoot;
    }

    function getWidgetRootSelector() {
      return `[${MIWRID_ATTRIBUTE_NAME}="${widgetRootId}"]`;
    }

    function onRootAdded(callback: RootAddedCallback) {
      rootAddedCallbacksRef.current.add(callback);

      return function removeRootAddedCallback() {
        rootAddedCallbacksRef.current.delete(callback);
      };
    }

    function onRootRemoved(callback: RootRemovedCallback) {
      rootRemovedCallbacksRef.current.add(callback);

      return function removeRootRemovedCallback() {
        rootRemovedCallbacksRef.current.delete(callback);
      };
    }

    return {
      addRoot,
      getRoots,
      getWidgetRoot,
      getWidgetRootSelector,
      onRootAdded,
      onRootRemoved
    };
  }, [widgetRoot, widgetRootId]);

  useEffect(() => {
    widgetRoot.setAttribute(MIWRID_ATTRIBUTE_NAME, widgetRootId);
    return api.addRoot(widgetRoot);
  }, [api, widgetRoot]);

  return (
    <MountingInventoryAPIContext.Provider value={api}>
      {children}
    </MountingInventoryAPIContext.Provider>
  );
}

export interface MountingInventoryAPI {
  addRoot(root: Element | ShadowRoot): RemoveRootFn;
  getRoots(): SetIterator<Element | ShadowRoot>;
  getWidgetRoot(): Element;
  getWidgetRootSelector(): string;
  onRootAdded(callback: RootAddedCallback): RemoveRootAddedCallbackFn;
  onRootRemoved(callback: RootRemovedCallback): RemoveRootRemovedCallbackFn;
}

export type RemoveRootFn = () => void;

export type RootAddedCallback = (root: Element | ShadowRoot) => void;

export type RootRemovedCallback = (root: Element | ShadowRoot) => void;

export type RemoveRootAddedCallbackFn = () => void;

export type RemoveRootRemovedCallbackFn = () => void;

const MountingInventoryAPIContext = createContext<
  MountingInventoryAPI | undefined
>(undefined);

export function useMountingInventoryAPI() {
  return useContext(MountingInventoryAPIContext);
}
